import React from 'react';
import { min } from 'lodash';
import { PaginationWrapper, InfinitePaginationButton, SimplePaginationWrapper, PageSwitcher, Page } from './styled';
import { SmallText, Text } from 'ds/components/typography';
import ChevronDown from 'assets/svg/chevron-down.svg';
import { useLocale } from 'locale';
import { useScreenBreakpoint } from 'consts/breakpoints';

const MOBILE_MAX_PAGES = 5;
const DESKTOP_MAX_PAGES = 7;
const WIDE_STYLE_THRESHOLD = 99;
const DOTS = '...';

export type PaginationType = 'infinite' | 'pages';

export interface PageItemRendererOptions {
  page: number;
  isActive: boolean;
  isWide: boolean;
  onClick: () => void;
}
export type PageItemRenderer = (options: PageItemRendererOptions) => JSX.Element;

export interface PageSwitcherRendererOptions {
  page: number;
  type: 'next' | 'prev';
  isDisabled: boolean;
  onClick: () => void;
}
export type PageSwitcherRenderer = (options: PageSwitcherRendererOptions) => JSX.Element;

interface PaginationProps {
  activePage: number;
  paginationType: PaginationType;
  totalPages: number;
  onSetActivePage: (page: number) => void;
  renderPageItem?: PageItemRenderer;
  renderPageSwitcher?: PageSwitcherRenderer;
  maxPagesToShow?: number;
}

export const Pagination: React.FC<PaginationProps> = ({
  paginationType,
  activePage,
  onSetActivePage,
  totalPages,
  renderPageItem,
  renderPageSwitcher,
  maxPagesToShow,
}) => {
  const { t } = useLocale();

  const handlePrevPage = () => onSetActivePage(activePage - 1);
  const handleNextPage = () => onSetActivePage(activePage + 1);
  const maxPages = maxPagesToShow || (useScreenBreakpoint() > 2 ? DESKTOP_MAX_PAGES : MOBILE_MAX_PAGES);
  const pages = min([ maxPages, totalPages ]) - 2;
  const middlePage = Math.ceil(maxPages / 2);
  const isWide = totalPages > WIDE_STYLE_THRESHOLD;
  const hasPreviousPage = activePage - 1 >= 1;
  const hasNextPage = activePage + 1 <= totalPages;
  let showStartDots = false;
  let showEndDots = false;
  let firstPage = 2;

  if (totalPages > maxPages) {
    showStartDots = activePage > middlePage;
    showEndDots = activePage <= totalPages - middlePage;

    if (showStartDots && !showEndDots) {
      firstPage = totalPages - pages;
    }
    else if (showStartDots && showEndDots) {
      firstPage = pages === 1 ? activePage : activePage - pages + Math.ceil(pages / 2);
    }
  }

  return (
    <PaginationWrapper data-auto="bulletins-pagination" className="pagination">
      {paginationType === 'infinite' ? (
        activePage !== totalPages ? (
          <InfinitePaginationButton onClick={handleNextPage}>
            <SmallText>{t('table.loadMore')}</SmallText>
          </InfinitePaginationButton>
        ) : null
      ) : (
        <SimplePaginationWrapper>
          {renderPageSwitcher({
            page: hasPreviousPage ? activePage - 1 : activePage,
            isDisabled: !hasPreviousPage,
            type: 'prev',
            onClick: handlePrevPage,
          })}

          {renderPageItem({
            page: 1,
            isWide,
            isActive: activePage === 1,
            onClick: () => onSetActivePage(1),
          })}

          {Array(pages).fill(null).map((it, id) => (
            <div key={id}>
              {id === 0 && showStartDots || id === pages - 1 && showEndDots ? (
                <Page onlyIcon={!isWide} isActive={false} isDots={true} isWide={isWide}>
                  <Text>{DOTS}</Text>
                </Page>
              ) : (
                renderPageItem({
                  page: firstPage + id,
                  isWide,
                  isActive: firstPage + id === activePage,
                  onClick: () => onSetActivePage(firstPage + id),
                })
              )}
            </div>
          ))}

          {renderPageItem({
            page: totalPages,
            isWide,
            isActive: activePage === totalPages,
            onClick: () => onSetActivePage(totalPages),
          })}

          {renderPageSwitcher({
            page: hasNextPage ? activePage + 1 : activePage,
            type: 'next',
            isDisabled: !hasNextPage,
            onClick: handleNextPage,
          })}
        </SimplePaginationWrapper>
      )}
    </PaginationWrapper>
  );
};

const defaultRenderPageItem: PageItemRenderer = ({ page, isActive, isWide, onClick }) => (
  <Page onlyIcon={!isWide} isActive={isActive} isWide={isWide} onClick={onClick}>
    <Text>{page}</Text>
  </Page>
);

const defaultRenderPageSwitcher: PageSwitcherRenderer = ({ type, isDisabled, onClick }) => (
  <PageSwitcher onlyIcon mode="ghost" arrowType={type} disabled={isDisabled} onClick={onClick}>
    <ChevronDown width={24} height={24} />
  </PageSwitcher>
);

Pagination.defaultProps = {
  renderPageItem: defaultRenderPageItem,
  renderPageSwitcher: defaultRenderPageSwitcher,
};
